import React from "react";
import {
  Users,
  DollarSign,
  TrendingUp,
  Search,
  Plus,
  CreditCard,
  EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { requiresActivation } from "../../constants/plans";
import { ACTIVATION_UI_COPY } from "../../constants/uiCopy";
import { NAV_LABELS } from "../../constants/uiCopy";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BrokerDashboard = ({
  user,
  brokerLeads,
  leadsFunnel,
  commissions,
  managedProperties,
  representedDevelopments,
  showLeadModal,
  setShowLeadModal,
  showCommissionModal,
  setShowCommissionModal,
  updateLeadStatus,
  notifyQualifiedLead,
  updateCommissionStatus,
  deleteCommission,
  navigate,
}) => {
  if (user?.role !== "broker") return null;

  const closedLeadsCount = brokerLeads.filter(
    (l) => l.status === "cerrado_ganado" || l.status === "cerrado_perdido",
  ).length;

  const leadsByStatus = {
    nuevo: brokerLeads.filter((l) => l.status === "nuevo").length,
    contactado: brokerLeads.filter((l) => l.status === "contactado").length,
    visitado: brokerLeads.filter((l) => l.status === "visitado").length,
    propuesta: brokerLeads.filter((l) => l.status === "propuesta").length,
    en_negociacion: brokerLeads.filter((l) => l.status === "en_negociacion")
      .length,
    cerrado: closedLeadsCount,
  };

  const isPending = requiresActivation(user);

  return (
    <>
      {/* Aviso perfil oculto */}
      {isPending && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <EyeOff className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Tu perfil no está visible en la plataforma</p>
            <p className="text-amber-700 mt-0.5">
              {ACTIVATION_UI_COPY.brokerPendingProfile}
            </p>
          </div>
        </div>
      )}

      {/* Embudo de Leads */}
      {leadsFunnel && (
        <div
          className="bg-background border border-border rounded-lg p-4 sm:p-6 mb-6"
          data-testid="leads-funnel"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <h2
                className="text-lg font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Embudo de Leads
              </h2>
            </div>
            <Button
              size="sm"
              onClick={() => setShowLeadModal(true)}
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuevo Lead
            </Button>
          </div>

          <div className="space-y-2">
            {[
              {
                key: "nuevo",
                label: "Nuevos",
                color: "bg-blue-500",
                count: leadsByStatus.nuevo,
              },
              {
                key: "contactado",
                label: "Contactados",
                color: "bg-yellow-500",
                count: leadsByStatus.contactado,
              },
              {
                key: "visitado",
                label: "Visitados",
                color: "bg-orange-500",
                count: leadsByStatus.visitado,
              },
              {
                key: "propuesta",
                label: "Propuesta",
                color: "bg-indigo-500",
                count: leadsByStatus.propuesta,
              },
              {
                key: "en_negociacion",
                label: "Negociación",
                color: "bg-purple-500",
                count: leadsByStatus.en_negociacion,
              },
              {
                key: "cerrado",
                label: "Cerrados",
                color: "bg-green-500",
                count: leadsByStatus.cerrado,
              },
            ].map((stage) => (
              <div key={stage.key} className="flex items-center gap-3">
                <span className="text-xs w-24 text-right text-muted-foreground">
                  {stage.label}
                </span>
                <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
                  <div
                    className={`${stage.color} h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500`}
                    style={{
                      width: `${Math.max(brokerLeads.length > 0 ? (stage.count / brokerLeads.length) * 100 : 0, 8)}%`,
                    }}
                  >
                    <span className="text-xs text-white font-bold">
                      {stage.count}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Panel Resumen Broker */}
      <div
        className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg p-4 sm:p-6 mb-6 text-white"
        data-testid="broker-summary"
      >
        <h2
          className="text-xl sm:text-2xl font-bold mb-4"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Resumen comercial del asesor
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold">
              {managedProperties.length}
            </p>
            <p className="text-[10px] sm:text-xs opacity-80">
              Propiedades Co-Representadas
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold">
              {brokerLeads.length}
            </p>
            <p className="text-[10px] sm:text-xs opacity-80">Leads Totales</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold">
              {commissions.length}
            </p>
            <p className="text-[10px] sm:text-xs opacity-80">Comisiones</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 text-center">
            <p className="text-2xl sm:text-3xl font-bold">
              $
              {commissions
                .filter((c) => c.status === "pagada")
                .reduce((sum, c) => sum + (c.commission_amount || 0), 0)
                .toLocaleString()}
            </p>
            <p className="text-[10px] sm:text-xs opacity-80">
              Comisiones Cobradas
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/inventario-brokers")}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            {`Ver ${NAV_LABELS.collaborationInventory}`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCommissionModal(true)}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <DollarSign className="w-4 h-4 mr-1" /> Registrar Comisión
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowLeadModal(true)}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <Plus className="w-4 h-4 mr-1" /> Nuevo Lead
          </Button>
        </div>
      </div>

      {/* Desarrollos Representados */}
      {representedDevelopments.length > 0 && (
        <div
          className="bg-background border border-border rounded-lg p-4 sm:p-6 mb-6"
          data-testid="represented-developments"
        >
          <h2
            className="text-lg sm:text-xl font-bold mb-4 flex items-center gap-2"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          >
            <CreditCard className="w-5 h-5 text-primary" />
            Desarrollos Representados ({representedDevelopments.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[400px] overflow-y-auto">
            {representedDevelopments.map((dev) => (
              <div
                key={dev.development_id}
                className="border border-border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
              >
                <h3 className="font-semibold text-base sm:text-lg mb-2 truncate">
                  {dev.development_name}
                </h3>
                <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-3">
                  <span>
                    Total: {dev.total_units ?? dev.units_summary?.total ?? 0}
                  </span>
                  <span className="text-green-600">
                    Disponibles:{" "}
                    {dev.available_units ?? dev.units_summary?.disponibles ?? 0}
                  </span>
                  <span className="text-yellow-600">
                    Apartadas:{" "}
                    {dev.reserved_units ?? dev.units_summary?.apartadas ?? 0}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/development/${dev.development_id}`)}
                >
                  Ver Desarrollo
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Broker Accordion Sections */}
      <Accordion type="multiple" className="space-y-3 mb-6">
        {/* Propiedades Co-Representadas */}
        <AccordionItem
          value="managed-properties"
          className="bg-background border border-border rounded-lg"
        >
          <AccordionTrigger className="px-4 sm:px-6 py-4">
            <span className="font-bold text-sm sm:text-base">
              Propiedades Co-Representadas ({managedProperties.length})
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="px-4 sm:px-6 pb-4 max-h-[500px] overflow-y-auto">
              <p className="text-sm text-muted-foreground mb-4">
                Propiedades donde tienes representación aprobada para co-listing
              </p>
              {managedProperties.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground mb-2">
                    No tienes propiedades gestionadas aún
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/inventario-brokers")}
                  >
                    {`Explorar ${NAV_LABELS.collaborationInventory}`}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {managedProperties.map((prop) => (
                    <div
                      key={prop.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">
                          {prop.title}
                        </h4>
                        <p className="text-xs text-muted-foreground truncate">
                          {prop.address}
                        </p>
                        <p className="text-sm font-bold mt-1">
                          ${(prop.price_mxn || 0).toLocaleString()} MXN
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/property/${prop.id}`)}
                        className="w-full sm:w-auto"
                      >
                        Ver
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Tracking de Comisiones */}
        <AccordionItem
          value="commissions"
          className="bg-background border border-border rounded-lg"
        >
          <AccordionTrigger className="px-4 sm:px-6 py-4">
            <span className="font-bold text-sm sm:text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Tracking de Comisiones ({commissions.length})
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="px-4 sm:px-6 pb-4 max-h-[500px] overflow-y-auto">
              <div className="flex justify-end mb-4">
                <Button onClick={() => setShowCommissionModal(true)} size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Nueva Comisión
                </Button>
              </div>
              {commissions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">
                    No hay comisiones registradas
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {commissions.map((commission) => (
                    <div
                      key={commission.id}
                      className="border border-border rounded-lg p-3 sm:p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <h4 className="font-medium text-sm truncate">
                            {commission.property_title}
                          </h4>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            Venta: $
                            {(commission.sale_amount || 0).toLocaleString()}{" "}
                            {commission.currency}
                          </p>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium ${
                            commission.status === "pendiente"
                              ? "bg-yellow-100 text-yellow-700"
                              : commission.status === "pagada"
                                ? "bg-green-100 text-green-700"
                                : commission.status === "en_proceso"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {commission.status}
                        </span>
                      </div>
                      <p className="text-lg font-bold text-green-600 mb-2">
                        Comisión: $
                        {(commission.commission_amount || 0).toLocaleString()}{" "}
                        {commission.currency}
                      </p>
                      <div className="flex gap-2">
                        {commission.status === "pendiente" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateCommissionStatus(
                                commission.id,
                                "en_proceso",
                              )
                            }
                          >
                            En Proceso
                          </Button>
                        )}
                        {commission.status === "en_proceso" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateCommissionStatus(commission.id, "pagada")
                            }
                          >
                            Marcar Pagada
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deleteCommission(commission.id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};

export { BrokerDashboard };
